import { useNavigate } from "react-router-dom";
import { fmtDateTime } from "@/lib/constants";
import {
  Plus, Pencil, ArrowLeftRight, Trash2, UserCog, ImagePlus,
  Power, AlertTriangle, Layers, Activity,
} from "lucide-react";

const ACTION_META = {
  create: { icon: Plus, tone: "text-emerald-600 bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-400", verb: "created" },
  update: { icon: Pencil, tone: "text-amber-600 bg-amber-100 dark:bg-amber-950/60 dark:text-amber-400", verb: "updated" },
  status_change: { icon: ArrowLeftRight, tone: "text-blue-600 bg-blue-100 dark:bg-blue-950/60 dark:text-blue-400", verb: "changed status of" },
  delete: { icon: Trash2, tone: "text-red-600 bg-red-100 dark:bg-red-950/60 dark:text-red-400", verb: "deleted" },
  assign: { icon: UserCog, tone: "text-violet-600 bg-violet-100 dark:bg-violet-950/60 dark:text-violet-400", verb: "assigned" },
  upload: { icon: ImagePlus, tone: "text-cyan-600 bg-cyan-100 dark:bg-cyan-950/60 dark:text-cyan-400", verb: "uploaded photos to" },
  bulk_update: { icon: Layers, tone: "text-amber-600 bg-amber-100 dark:bg-amber-950/60 dark:text-amber-400", verb: "bulk-updated" },
  activate: { icon: Power, tone: "text-emerald-600 bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-400", verb: "activated" },
  deactivate: { icon: Power, tone: "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400", verb: "deactivated" },
  purge: { icon: AlertTriangle, tone: "text-red-600 bg-red-100 dark:bg-red-950/60 dark:text-red-400", verb: "purged" },
};

const DEFAULT_META = { icon: Activity, tone: "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400", verb: "" };

export function actionLabel(a) {
  return (ACTION_META[a] || {}).verb || (a || "").replace(/_/g, " ");
}

export default function ActivityRow({ log, compact = false, onActorClick }) {
  const navigate = useNavigate();
  const meta = ACTION_META[log.action] || DEFAULT_META;
  const Icon = meta.icon;

  const target =
    log.entity === "job" ? `/jobs/${log.entity_id}` :
    log.entity === "customer" ? `/customers/${log.entity_id}` :
    log.entity === "user" ? `/staff` : null;
  const clickable = !!target && log.action !== "delete" && log.action !== "purge";

  const summary = log.details || `${actionLabel(log.action)} ${log.entity}`;
  const actorName = log.actor_name || log.actor_email || "Someone";

  return (
    <div
      data-testid={`activity-row-${log.id}`}
      onClick={clickable ? () => navigate(target) : undefined}
      className={`flex items-start gap-3 rounded-xl border bg-card px-3 py-2.5 ${clickable ? "cursor-pointer hover:bg-accent/50" : ""} transition-colors`}
    >
      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.tone}`}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug">
          {onActorClick && log.actor_id ? (
            <button
              type="button"
              data-testid={`activity-actor-${log.id}`}
              onClick={(e) => { e.stopPropagation(); onActorClick(log.actor_id, actorName); }}
              className="font-semibold text-primary hover:underline underline-offset-2"
            >
              {actorName}
            </button>
          ) : (
            <span className="font-semibold">{actorName}</span>
          )}{" "}
          <span className="text-muted-foreground">{actionLabel(log.action)}</span>{" "}
          <span className="font-medium capitalize">{log.entity}</span>
        </p>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{summary}</p>
        {!compact && (
          <p className="text-[11px] text-muted-foreground/70 mt-0.5">{fmtDateTime(log.created_at)}</p>
        )}
      </div>
      {compact && (
        <span className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">{fmtDateTime(log.created_at)}</span>
      )}
    </div>
  );
}
